import { ErrorName, PlayerError } from '../../errors';
import { debug } from '../../utils/logger';
import BaseEngine from '../BaseEngine';
import { createFingerprint, parseId3Tags } from './parseId3';
const DUP_CACHE_SIZE = 128;
export default class HLSEngine extends BaseEngine {
    static name = 'hls';
    static HlsJs;
    static canPlayType(mimeType) {
        switch (mimeType) {
            case 'audio/x-mpegurl':
            case 'application/x-mpegurl':
            case 'application/vnd.apple.mpegurl':
                return true;
            default:
                return false;
        }
    }
    static async loadHlsJs() {
        if (!HLSEngine.HlsJs) {
            HLSEngine.HlsJs = await import('hls.js').then((module) => module.default);
        }
        return HLSEngine.HlsJs;
    }
    #hls;
    #isLive = true;
    #defaultStartPosition = 0;
    #id3FingerprintSet = new Set();
    #id3FingerprintQueue = [];
    constructor(options, defaultStartPosition) {
        super(options);
        if (defaultStartPosition) {
            this.#defaultStartPosition = defaultStartPosition;
        }
    }
    get #Events() {
        return HLSEngine.HlsJs.Events;
    }
    get #ErrorDetails() {
        return HLSEngine.HlsJs.ErrorDetails;
    }
    get #ErrorTypes() {
        return HLSEngine.HlsJs.ErrorTypes;
    }
    #onLoadError = (error) => {
        this.#deregisterHlsEvents();
        this.#hls?.destroy();
        this.emit('error', error || this.getElement()?.error);
    };
    async #loadHls() {
        try {
            const HlsJs = await HLSEngine.loadHlsJs();
            this.#hls = new HlsJs({
                debug: this._options.debug === 'verbose',
                autoStartLoad: false,
            });
        }
        catch {
            this.#onLoadError(new Error('The hls.js library failed to load'));
            throw undefined;
        }
    }
    #attachMedia() {
        return new Promise((resolve, reject) => {
            const resolver = () => {
                resolve();
                this.#hls?.off(this.#Events.MEDIA_ATTACHED, resolver);
                this.#hls?.off(this.#Events.ERROR, rejecter);
            };
            const rejecter = () => {
                reject();
                this.#hls?.off(this.#Events.MEDIA_ATTACHED, resolver);
                this.#hls?.off(this.#Events.ERROR, rejecter);
            };
            this.#hls?.once(this.#Events.MEDIA_ATTACHED, resolver);
            this.#hls?.once(this.#Events.ERROR, rejecter);
            this.#hls?.attachMedia(this._element);
        });
    }
    async #loadSource() {
        await new Promise((resolve, reject) => {
            const resolver = () => {
                resolve();
                this.#hls?.off(this.#Events.MANIFEST_PARSED, resolver);
                this.#hls?.off(this.#Events.ERROR, rejecter);
            };
            const rejecter = () => {
                reject();
                this.#hls?.off(this.#Events.MANIFEST_PARSED, resolver);
                this.#hls?.off(this.#Events.ERROR, rejecter);
            };
            this.#hls?.once(this.#Events.MANIFEST_PARSED, resolver);
            this.#hls?.once(this.#Events.ERROR, rejecter);
            this.#hls?.loadSource(this._options.src);
        });
        this.#hls?.startLoad(this._options.startPosition ?? this.#defaultStartPosition);
    }
    async load() {
        if (!this._element) {
            return;
        }
        await this.#loadHls();
        return new Promise(async (resolve, reject) => {
            const onError = (event, data) => {
                this.#onError(event, data);
                reject();
            };
            this.#hls?.once(this.#Events.ERROR, onError);
            this.#hls?.once(this.#Events.LEVEL_LOADED, this.#onLevelLoaded);
            try {
                await Promise.all([this.#loadSource(), this.#attachMedia()]);
                resolve();
            }
            catch (e) {
                this.#onLoadError(e);
                reject();
            }
            this.#hls?.off(this.#Events.ERROR, onError);
        });
    }
    play() {
        this.#registerHlsEvents();
        return super.play();
    }
    destroy() {
        this.#deregisterHlsEvents();
        this.#hls?.stopLoad();
        this.#hls?.detachMedia();
        this.#hls?.destroy();
        super.destroy();
    }
    isLive() {
        return this.#isLive;
    }
    #onLevelLoaded = (_, data) => {
        this.#isLive = data.details.live ?? true;
    };
    #onFragLoaded = (_event, data) => {
        this.emit('meta', {
            tags: data.frag.tagList.map((tag) => ({
                key: tag[0],
                value: tag[1],
            })),
        });
    };
    #onFragParsingMetadata = (_event, data) => {
        if (!Array.isArray(data?.samples)) {
            return;
        }
        for (const sample of data.samples) {
            if (!Number.isFinite(sample.pts)) {
                continue;
            }
            try {
                const id3 = parseId3Tags(sample.data);
                const fingerprint = createFingerprint(id3);
                if (this.#id3FingerprintSet.has(fingerprint)) {
                    continue;
                }
                this.#id3FingerprintSet.add(fingerprint);
                this.#id3FingerprintQueue.push(fingerprint);
                if (this.#id3FingerprintQueue.length > DUP_CACHE_SIZE) {
                    const oldestFingerprint = this.#id3FingerprintQueue.shift();
                    if (oldestFingerprint) {
                        this.#id3FingerprintSet.delete(oldestFingerprint);
                    }
                }
                this.emit('metadata', {
                    id3,
                    startSeconds: Math.max(sample.pts, 0),
                });
            }
            catch {
            }
        }
    };
    #onErrorDetails(details) {
        switch (details) {
            case this.#ErrorDetails.FRAG_LOAD_ERROR: {
                debug('[HLS] FRAG_LOAD_ERROR', details);
                break;
            }
            case this.#ErrorDetails.BUFFER_STALLED_ERROR: {
                debug('[HLS] BUFFER_STALLED_ERROR', details);
                break;
            }
            default:
                break;
        }
    }
    #onError = (event, data) => {
        debug('[HLS] Error', data);
        if (data?.details) {
            this.#onErrorDetails(data.details);
        }
        if (!data?.fatal) {
            return;
        }
        if (data.type === this.#ErrorTypes.MEDIA_ERROR) {
            debug('[HLS] Fatal media error encountered, try to recover', event);
            this.#hls?.recoverMediaError();
            return;
        }
        if (data.type === this.#ErrorTypes.NETWORK_ERROR && this.isPlaying()) {
            this.#deregisterHlsEvents();
            this.#hls?.destroy();
            this.emit('error', PlayerError.createError(ErrorName.PlaybackErrorNetwork));
            return;
        }
        const { type, details, response, error } = data || {};
        this.#deregisterHlsEvents();
        this.#hls?.destroy();
        this.emit('error', { type, details, response, message: error?.message });
    };
    #registerHlsEvents() {
        this.#hls?.on(this.#Events.ERROR, this.#onError);
        this.#hls?.on(this.#Events.LEVEL_LOADED, this.#onLevelLoaded);
        this.#hls?.on(this.#Events.FRAG_LOADED, this.#onFragLoaded);
        this.#hls?.on(this.#Events.FRAG_PARSING_METADATA, this.#onFragParsingMetadata);
    }
    #deregisterHlsEvents() {
        this.#hls?.off(this.#Events.ERROR, this.#onError);
        this.#hls?.off(this.#Events.LEVEL_LOADED, this.#onLevelLoaded);
        this.#hls?.off(this.#Events.FRAG_LOADED, this.#onFragLoaded);
        this.#hls?.off(this.#Events.FRAG_PARSING_METADATA, this.#onFragParsingMetadata);
    }
}
